// store/slices/commonModalSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import { LegalityScreens } from "../../common/types/enums";

export interface ModalState {
  cartModal: {
    isOpen: boolean;
  };
  complexOrderCartModal: {
    isOpen: boolean;
  };
  ratingModal: {
    isOpen: boolean;
    itemId?: number;
    itemName?: string;
  };
  legalityModal: {
    isOpen: boolean;
    clusterId?: number;
    clusterName?: string;
    screen: LegalityScreens;
  };
  serviceModal: {
    isOpen: boolean;
  };
}

const initialState: ModalState = {
  cartModal: {
    isOpen: false,
  },
  complexOrderCartModal: {
    isOpen: false,
  },
  ratingModal: {
    isOpen: false,
    itemId: undefined,
    itemName: undefined,
  },
  legalityModal: {
    isOpen: false,
    clusterId: undefined,
    clusterName: undefined,
    screen: LegalityScreens.USER_AGREEMENT,
  },
  serviceModal: {
    isOpen: false,
  },
};

const commonModalSlice = createSlice({
  name: "commonModal",
  initialState,
  reducers: {
    openCartModal: (state) => {
      state.cartModal.isOpen = true;
    },
    closeCartModal: (state) => {
      state.cartModal.isOpen = false;
    },
    toggleCartModal: (state) => {
      state.cartModal.isOpen = !state.cartModal.isOpen;
    },

    openComplexOrderCartModal: (state) => {
      state.complexOrderCartModal.isOpen = true;
    },
    closeComplexOrderCartModal: (state) => {
      state.complexOrderCartModal.isOpen = false;
    },
    toggleComplexOrderCartModal: (state) => {
      state.complexOrderCartModal.isOpen = !state.complexOrderCartModal.isOpen;
    },

    openRatingModal: (
      state,
      action: PayloadAction<{ itemId: number; itemName?: string }>,
    ) => {
      state.ratingModal.isOpen = true;
      state.ratingModal.itemId = action.payload.itemId;
      state.ratingModal.itemName = action.payload.itemName;
    },
    closeRatingModal: (state) => {
      state.ratingModal.isOpen = false;
      state.ratingModal.itemId = undefined;
      state.ratingModal.itemName = undefined;
    },

    openLegalityModal: (
      state,
      action: PayloadAction<{
        clusterId: number;
        clusterName?: string;
        screen?: LegalityScreens;
      }>,
    ) => {
      state.legalityModal.isOpen = true;
      state.legalityModal.clusterId = action.payload.clusterId;
      state.legalityModal.clusterName = action.payload.clusterName;
      if (action.payload.screen !== undefined) {
        state.legalityModal.screen = action.payload.screen;
      }
    },
    closeLegalityModal: (state) => {
      state.legalityModal.isOpen = false;
      state.legalityModal.clusterId = undefined;
      state.legalityModal.clusterName = undefined;
      state.legalityModal.screen = LegalityScreens.USER_AGREEMENT;
    },
    setLegalityScreen: (state, action: PayloadAction<LegalityScreens>) => {
      state.legalityModal.screen = action.payload;
    },

    openServiceModal: (state) => {
      state.serviceModal.isOpen = true;
    },
    closeServiceModal: (state) => {
      state.serviceModal.isOpen = false;
    },
  },
});

export const {
  openCartModal,
  closeCartModal,
  toggleCartModal,
  openComplexOrderCartModal,
  closeComplexOrderCartModal,
  toggleComplexOrderCartModal,
  openRatingModal,
  closeRatingModal,
  openLegalityModal,
  closeLegalityModal,
  setLegalityScreen,
  openServiceModal,
  closeServiceModal,
} = commonModalSlice.actions;

export const selectModalState = (state: RootState) => state.commonModal;

export const selectCartModal = (state: RootState) =>
  state.commonModal.cartModal;
export const selectComplexOrderCartModal = (state: RootState) =>
  state.commonModal.complexOrderCartModal;
export const selectRatingModal = (state: RootState) =>
  state.commonModal.ratingModal;
export const selectLegalityModal = (state: RootState) =>
  state.commonModal.legalityModal;
export const selectServiceModal = (state: RootState) =>
  state.commonModal.serviceModal;

export const selectIsCartModalOpen = (state: RootState) =>
  state.commonModal.cartModal.isOpen;
export const selectIsComplexOrderCartModalOpen = (state: RootState) =>
  state.commonModal.complexOrderCartModal.isOpen;
export const selectIsRatingModalOpen = (state: RootState) =>
  state.commonModal.ratingModal.isOpen;
export const selectRatingModalItemId = (state: RootState) =>
  state.commonModal.ratingModal.itemId;
export const selectRatingModalItemName = (state: RootState) =>
  state.commonModal.ratingModal.itemName;

export const selectIsLegalityModalOpen = (state: RootState) =>
  state.commonModal.legalityModal.isOpen;
export const selectLegalityClusterId = (state: RootState) =>
  state.commonModal.legalityModal.clusterId;
export const selectLegalityClusterName = (state: RootState) =>
  state.commonModal.legalityModal.clusterName;
export const selectLegalityScreen = (state: RootState) =>
  state.commonModal.legalityModal.screen;

export const selectIsServiceModalOpen = (state: RootState) =>
  state.commonModal.serviceModal.isOpen;

export default commonModalSlice.reducer;
